import { Search } from '@components/shared/search/Search';
import type { CampaignType } from '@features/campaign-list';
import { campaignTypeLabels } from '@features/campaign-list';
import type { Client } from '@features/clients';
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
} from '@mui/material';
import type { ExportFilterState } from './filter-state';

const isCampaignType = (value: string): value is CampaignType =>
  value in campaignTypeLabels;

type ExportFiltersProps = {
  clients: Client[];
  filterState: ExportFilterState;
  isLoading: boolean;
  onFilterChange: (state: ExportFilterState) => void;
};

export const ExportFilters = ({
  clients,
  filterState,
  isLoading,
  onFilterChange: handleFilterChange,
}: ExportFiltersProps) => {
  const {
    selectedClient,
    selectedAdvertiser,
    selectedCampaignType,
    searchText,
  } = filterState;

  const advertisers = selectedClient?.advertisers ?? [];

  const handleClientChange = (clientId: number) => {
    const client = clients.find(c => c.id === clientId) ?? null;
    handleFilterChange({
      ...filterState,
      selectedClient: client,
      selectedAdvertiser: null,
    });
  };

  const handleAdvertiserChange = (advertiserId: number) => {
    const advertiser = advertisers.find(a => a.id === advertiserId) ?? null;
    handleFilterChange({ ...filterState, selectedAdvertiser: advertiser });
  };

  const handleCampaignTypeChange = (value: string) => {
    if (isCampaignType(value)) {
      handleFilterChange({ ...filterState, selectedCampaignType: value });
    }
  };

  return (
    <Stack alignItems="center" direction="row" gap={2} marginTop={2}>
      <FormControl size="small" sx={{ minWidth: 180 }}>
        <InputLabel>Organization</InputLabel>
        <Select
          data-testid="organization-select"
          disabled={isLoading || clients.length <= 1}
          label="Organization"
          onChange={e => handleClientChange(Number(e.target.value))}
          value={selectedClient?.id ?? ''}
        >
          {clients.map(client => (
            <MenuItem key={client.id} value={client.id}>
              {client.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl size="small" sx={{ minWidth: 180 }}>
        <InputLabel>Advertiser</InputLabel>
        <Select
          data-testid="advertiser-select"
          disabled={advertisers.length === 0}
          label="Advertiser"
          onChange={e => handleAdvertiserChange(Number(e.target.value))}
          value={selectedAdvertiser?.id ?? ''}
        >
          {advertisers.map(advertiser => (
            <MenuItem key={advertiser.id} value={advertiser.id}>
              {advertiser.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl size="small" sx={{ minWidth: 180 }}>
        <InputLabel>Campaign Type</InputLabel>
        <Select
          data-testid="campaign-type-select"
          label="Campaign Type"
          onChange={e => handleCampaignTypeChange(e.target.value)}
          value={selectedCampaignType}
        >
          {Object.entries(campaignTypeLabels).map(([value, label]) => (
            <MenuItem key={value} value={value}>
              {label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Search
        onChange={e =>
          handleFilterChange({ ...filterState, searchText: e.target.value })
        }
        textFieldProps={{ sx: { marginLeft: 'auto', width: '20rem' } }}
        value={searchText}
      />
    </Stack>
  );
};
